import { useState } from "react";
import { Button, WizardSteps } from "../../../_components/ui";
import SplitEditorLayout from "../../../_components/layout/SplitEditorLayout";
import { UPSELL_WIZARD_STEPS } from "../constants";
import UpsellCustomerDetailsStep from "./steps/UpsellCustomerDetailsStep";
import UpsellPackageDetailsStep from "./steps/UpsellPackageDetailsStep";
import UpsellFinalizeStep from "./steps/UpsellFinalizeStep";
import UpsellReviewStep from "./steps/UpsellReviewStep";
import UpsellPreviewPane from "./UpsellPreviewPane";
import "./_upsell-wizard-body.scss";

/**
 * The wizard's actual step content, plus (when `showChrome` is true) its
 * own Back/Next/Submit nav. Mirrors
 * _features/proposals/components/ProposalWizardBody.jsx - context-
 * agnostic (no ModalShell/PageHeaderBar knowledge) so both the global-modal
 * host (UpsellFormModal) and the routed full-screen hosts
 * (UpsellCreatePage/EditPage/PreviewPage, all `showChrome={false}` - those
 * render Back/Next themselves inside their own header instead) render
 * identical step content.
 *
 * The step tabs + "Hide preview" toggle live here (not in either host's
 * header), same as ProposalWizardBody - `showPreview` is local state for
 * the same reason: it's this component's own concern.
 *
 * Steps 1-3 sit inside SplitEditorLayout with a live UpsellPreviewPane
 * alongside them. Step 4 (UpsellReviewStep) is deliberately NOT given the
 * split treatment - it's already its own full-width before/after
 * comparison (current subscription vs. new billing items), a more detailed
 * preview than the side pane could add anything to, and squeezing its
 * 2-column comparison grid into the split's ~1fr left column would make it
 * unreadable. Since there's no preview pane on step 4, the "Hide preview"
 * toggle only renders for steps 1-3.
 */
const UpsellWizardBody = ({
  wizard,
  catalogs,
  users = [],
  onAddSendToContact,
  showChrome = true,
}) => {
  const [showPreview, setShowPreview] = useState(true);

  const {
    control,
    watch,
    setValue,
    trigger,
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    onSubmit,
    isSubmitting,
    isEdit,
    isPreview,
  } = wizard;

  return (
    <fieldset className="upsell-wizard-body" disabled={isSubmitting}>
      <div className="upsell-header-below">
        <WizardSteps
          steps={UPSELL_WIZARD_STEPS}
          currentStep={currentStep}
          className="upsell-wizard-steps"
          onStepClick={goToStep}
        />
        {currentStep < 4 && (
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={() => setShowPreview((prev) => !prev)}
          >
            {showPreview ? "Hide preview" : "Show preview"}
          </Button>
        )}
      </div>

      <div className="upsell-wizard-body__content">
        {currentStep < 4 ? (
          <SplitEditorLayout
            left={
              <div className="upsell-wizard-body__content-inner">
                {currentStep === 1 && (
                  <UpsellCustomerDetailsStep
                    control={control}
                    setValue={setValue}
                    trigger={trigger}
                    watch={watch}
                    isPreview={isPreview}
                    lockCompany={wizard.lockCompany}
                  />
                )}
                {currentStep === 2 && (
                  <UpsellPackageDetailsStep
                    control={control}
                    watch={watch}
                    trigger={trigger}
                    wizard={wizard}
                    pricing={wizard.pricing}
                    catalogs={catalogs}
                    isPreview={isPreview}
                  />
                )}
                {currentStep === 3 && (
                  <UpsellFinalizeStep
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    users={users}
                    sendTo={watch("sendTo") || []}
                    onAddSendToContact={onAddSendToContact}
                    onRemoveSendToContact={wizard.removeSendToContact}
                    isEdit={isEdit}
                    isPreview={isPreview}
                  />
                )}
              </div>
            }
            preview={
              showPreview ? (
                <UpsellPreviewPane
                  control={control}
                  pricing={wizard.pricing}
                  selectedSubscription={wizard.selectedSubscription}
                />
              ) : null
            }
          />
        ) : (
          <div className="upsell-wizard-body__content-inner upsell-wizard-body__content-inner--full">
            <UpsellReviewStep
              watch={watch}
              selectedSubscription={wizard.selectedSubscription}
              billingItemsData={wizard.billingItemsData}
              totalAmountValue={wizard.totalAmountValue}
            />
          </div>
        )}
      </div>

      {showChrome && (
        <div className="upsell-wizard-body__nav">
          <Button
            type="button"
            variant="outlined"
            onClick={goToPreviousStep}
            disabled={currentStep === 1}
          >
            Back
          </Button>

          {currentStep < UPSELL_WIZARD_STEPS.length ? (
            <Button type="button" variant="contained" onClick={goToNextStep}>
              Next
            </Button>
          ) : !isPreview ? (
            <Button type="button" variant="contained" loading={isSubmitting} onClick={onSubmit}>
              {isEdit ? "Update upsell" : "Create upsell"}
            </Button>
          ) : null}
        </div>
      )}
    </fieldset>
  );
};

export default UpsellWizardBody;
